import { useSuspenseQuery } from '@tanstack/react-query';
import { Outlet, useParams } from 'react-router-dom';

import { accountLayoutQueryOptions } from '@lib/query/options/account';
import { mainLayoutQueryOptions } from '@lib/query/options/layout';
import AccountLayout from '@modules/account/templates/account-layout';
import LoginTemplate from '@modules/account/templates/login-template';
import { PendingTemplate } from '@modules/account/components/pending';
import { getAccountNavItems } from '@modules/account/utils/get-account-nav-items';

/**
 * Account Layout Wrapper
 *
 * This component replaces Next.js Parallel Routes (@dashboard, @login, @pending)
 * with React Router conditional rendering based on authentication state.
 *
 * Three-state logic:
 * - !isLoggedIn → Show LoginTemplate
 * - isLoggedIn && !isActive → Show PendingTemplate
 * - isLoggedIn && isActive → Show Dashboard (Outlet for child routes)
 */
export default function AccountLayoutWrapper() {
  const { countryCode } = useParams<{ countryCode: string }>();
  const { data: mainLayoutData } = useSuspenseQuery(mainLayoutQueryOptions(countryCode!));
  const { data: accountData } = useSuspenseQuery(accountLayoutQueryOptions());
  const { customer } = accountData;

  // State 1: Not logged in - show login page
  if (!mainLayoutData.isLoggedIn) {
    return (
      <AccountLayout
        customer={null}
        isLoggedIn={false}
        isActive={false}
        navItems={[]}
      >
        <LoginTemplate />
      </AccountLayout>
    );
  }

  // State 2: Logged in but not active (pending approval) - show pending page
  if (!mainLayoutData.isActive) {
    return (
      <AccountLayout
        customer={customer}
        isLoggedIn={true}
        isActive={false}
        navItems={[]}
      >
        <PendingTemplate />
      </AccountLayout>
    );
  }

  // State 3: Logged in and active - show dashboard with navigation
  const navItems = getAccountNavItems({
    paymentCodEnabled: mainLayoutData.paymentCodEnabled,
  });

  return (
    <AccountLayout
      customer={customer}
      isLoggedIn={true}
      isActive={true}
      navItems={navItems}
    >
      <Outlet />
    </AccountLayout>
  );
}
